(() => {
  if (window.PharmalynkCardPlans) return;
  const shop = document.currentScript?.dataset.shop;
  if (!shop) return;
  const cards = '[data-pl-fa-card], [data-pl-tf-card]';
  const requests = new Map();
  const states = new WeakMap();
  const queued = [];
  let active = 0;

  function drain() {
    while (active < 4 && queued.length) {
      active++;
      queued.shift()().finally(() => { active--; drain(); });
    }
  }

  function productData(id) {
    if (!requests.has(id)) {
      const request = new Promise((resolve, reject) => {
        queued.push(async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          try {
            const response = await fetch(`https://static.rechargecdn.com/store/${encodeURIComponent(shop)}/product/2022-06/${encodeURIComponent(id)}.json`, {
              signal: controller.signal,
              credentials: 'omit'
            });
            if (!response.ok) throw new Error('Autoship lookup failed');
            const payload = await response.json();
            if (!Array.isArray(payload.product?.plans)) throw new Error('Invalid Autoship data');
            resolve(payload.product);
          } catch (error) {
            requests.delete(id);
            reject(error);
          } finally {
            clearTimeout(timeout);
          }
        });
        drain();
      });
      requests.set(id, request);
    }
    return requests.get(id);
  }

  function eligiblePlans(product, variantId) {
    return product.plans.filter(plan =>
      ['subscription', 'prepaid', 'prepaid_v2', 'membership_subscription'].includes(plan.type)
      && plan.external_plan_id
      && (!plan.has_variant_restrictions
        || plan.external_variant_ids?.some(id => String(id) === String(variantId)))
    ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }

  function controls(card) {
    const prefix = card.hasAttribute('data-pl-tf-card') ? 'plTf' : 'plFa';
    const attribute = prefix === 'plTf' ? 'data-pl-tf' : 'data-pl-fa';
    return {
      prefix,
      select: card.querySelector(`[${attribute}-freq]`),
      autoship: card.querySelector(`[${attribute}-toggle="autoship"]`),
      onetime: card.querySelector(`[${attribute}-toggle="onetime"]`)
    };
  }

  function syncDisplay(card, select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    Object.assign(card.dataset, {
      sellingPlanId: option.value,
      spPrice: option.dataset.price,
      spPriceFormatted: option.dataset.priceFormatted,
      spDiscountPct: option.dataset.discountPct,
      spLabel: option.dataset.label,
      hasMultiFreq: String(select.options.length > 1)
    });
    card.querySelectorAll('[data-pl-fa-autoship-price], [data-pl-tf-autoship-price]').forEach(el => {
      el.textContent = option.dataset.priceFormatted;
    });
    card.querySelectorAll('[data-pl-fa-autoship-label], [data-pl-tf-autoship-label]').forEach(el => {
      el.textContent = option.dataset.label;
    });
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function showError(card) {
    let message = card.querySelector('[data-pl-card-plan-error]');
    if (!message) {
      message = document.createElement('p');
      message.setAttribute('data-pl-card-plan-error', '');
      message.setAttribute('role', 'status');
      message.textContent = 'Autoship options could not load. Please try again.';
      card.querySelector('[data-pl-fa-atc], [data-pl-tf-atc]')?.before(message);
    }
  }

  function ensure(card) {
    const variantId = card.dataset.variantId;
    const productId = card.dataset.productId;
    const previous = states.get(card);
    if (previous?.variantId === variantId && previous.status === 'ready') return Promise.resolve(true);
    if (previous?.variantId === variantId && previous.status === 'loading') return previous.promise;
    const { select, autoship, onetime } = controls(card);
    if (!select || !productId || !variantId) return Promise.resolve(true);
    const state = { variantId, status: 'loading' };
    const original = previous?.variantId === variantId
      ? previous.options
      : [...select.options].map(option => option.cloneNode(true));
    state.options = original;
    states.set(card, state);
    card.dataset.plPlanState = 'loading';
    autoship?.setAttribute('aria-busy', 'true');
    select.disabled = true;
    state.promise = productData(productId).then(product => {
      if (states.get(card) !== state || card.dataset.variantId !== variantId) return false;
      const options = new Map(original.map(option => [option.value, option]));
      const selected = select.value;
      const plans = eligiblePlans(product, variantId);
      const available = plans.flatMap(plan => {
        const option = options.get(String(plan.external_plan_id));
        if (!option) return [];
        const copy = option.cloneNode(true);
        copy.dataset.quantity = String(Math.max(1, Number(plan.product_quantity) || 1));
        return [copy];
      });
      select.replaceChildren(...available);
      if (available.some(option => option.value === selected)) select.value = selected;
      else if (available.length) select.selectedIndex = 0;
      if (!available.length) {
        delete card.dataset.sellingPlanId;
        if (onetime && !onetime.disabled) {
          onetime.click();
          card.dataset.mode = 'onetime';
          autoship.hidden = true;
          select.closest('.pl-fa__freq, .pl-tf__freq')?.setAttribute('hidden', '');
          card.classList.add('pl-fa__card--no-subscription');
        }
      } else {
        syncDisplay(card, select);
      }
      state.status = 'ready';
      card.dataset.plPlanState = 'ready';
      card.querySelector('[data-pl-card-plan-error]')?.remove();
      return true;
    }).catch(() => {
      state.status = 'error';
      card.dataset.plPlanState = 'error';
      return false;
    }).finally(() => {
      autoship?.removeAttribute('aria-busy');
      select.disabled = state.status !== 'ready';
    });
    return state.promise;
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      ensure(entry.target);
    });
  }, { rootMargin: '250px' });

  function scan(root) {
    const found = root.matches?.(cards) ? [root] : [...root.querySelectorAll(cards)];
    found.forEach(card => {
      if (card.dataset.plPlanObserved || !controls(card).select) return;
      card.dataset.plPlanObserved = 'true';
      observer.observe(card);
    });
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('[data-pl-fa-toggle="autoship"], [data-pl-tf-toggle="autoship"], [data-pl-fa-atc], [data-pl-tf-atc]');
    const card = target?.closest(cards);
    if (!card || target.disabled || !controls(card).select) return;
    const toggle = target.matches('[data-pl-fa-toggle="autoship"], [data-pl-tf-toggle="autoship"]');
    if (!toggle && card.dataset.mode !== 'autoship') return;
    const state = states.get(card);
    if (state?.status !== 'ready' || state.variantId !== card.dataset.variantId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      ensure(card).then(ready => {
        if (ready && card.isConnected && controls(card).select.options.length) target.click();
        else if (!ready) showError(card);
      });
      return;
    }
    const select = controls(card).select;
    if (!select.options.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

  }, true);

  scan(document);
  new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'attributes') {
        if (states.has(record.target)) ensure(record.target);
      } else record.addedNodes.forEach(node => { if (node instanceof Element) scan(node); });
    });
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-variant-id'] });
  window.PharmalynkCardPlans = { ensure };
})();
